import React, { useState, useCallback, useEffect, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { BaseConverter } from "../../BaseConverter";

// ─── Static Data ────────────────────────────────────────────────────────────

const CATEGORIES = [
  {
    id: "writing",
    label: "Writing & Content",
    icon: "bi-pencil-square",
    color: "primary",
    tasks: ["Blog post", "Email", "Social media caption", "Product description", "Press release", "Newsletter", "Essay", "Story / Fiction", "Script / Dialogue", "Cover letter"],
  },
  {
    id: "coding",
    label: "Coding & Dev",
    icon: "bi-code-slash",
    color: "success",
    tasks: ["Write code", "Debug / Fix bug", "Code review", "Explain code", "Write unit tests", "Refactor code", "Write documentation", "Architecture advice", "SQL query", "Regex pattern"],
  },
  {
    id: "image",
    label: "Image Generation",
    icon: "bi-image",
    color: "warning",
    tasks: ["Portrait / Person", "Landscape / Scene", "Product photo", "Logo / Icon", "Illustration", "Architecture", "Abstract art", "Fantasy / Sci-fi", "Food photography", "Pattern / Texture"],
  },
  {
    id: "analysis",
    label: "Analysis & Research",
    icon: "bi-bar-chart",
    color: "info",
    tasks: ["Summarise document", "Compare options", "Market research", "SWOT analysis", "Literature review", "Data interpretation", "Pros and cons", "Fact check", "Competitive analysis", "Risk assessment"],
  },
  {
    id: "marketing",
    label: "Marketing & SEO",
    icon: "bi-megaphone",
    color: "danger",
    tasks: ["Ad copy", "SEO meta description", "Landing page copy", "Call to action", "Brand voice guide", "Keyword strategy", "Campaign idea", "Tagline / Slogan", "Sales pitch", "Case study"],
  },
  {
    id: "education",
    label: "Education & Learning",
    icon: "bi-book",
    color: "secondary",
    tasks: ["Explain a concept", "Create quiz questions", "Lesson plan", "Study guide", "Simplify text", "Analogies", "Flashcards", "Textbook summary", "Homework help", "Debate prep"],
  },
  {
    id: "business",
    label: "Business & Strategy",
    icon: "bi-briefcase",
    color: "dark",
    tasks: ["Business plan section", "Meeting agenda", "Project proposal", "OKRs / Goals", "Job description", "Performance review", "Process documentation", "Investor pitch", "Strategic roadmap", "Policy draft"],
  },
  {
    id: "personal",
    label: "Personal & Creative",
    icon: "bi-person-heart",
    color: "primary",
    tasks: ["Life advice", "Travel itinerary", "Recipe / Meal plan", "Workout plan", "Gratitude journal", "Brainstorm ideas", "Gift ideas", "Party planning", "Book recommendations", "Budget plan"],
  },
];

const TONES = [
  { id: "professional", label: "Professional", icon: "💼" },
  { id: "casual", label: "Casual", icon: "😊" },
  { id: "persuasive", label: "Persuasive", icon: "🎯" },
  { id: "educational", label: "Educational", icon: "📚" },
  { id: "humorous", label: "Humorous", icon: "😄" },
  { id: "empathetic", label: "Empathetic", icon: "💙" },
  { id: "concise", label: "Concise", icon: "⚡" },
  { id: "creative", label: "Creative", icon: "✨" },
  { id: "authoritative", label: "Authoritative", icon: "👑" },
  { id: "storytelling", label: "Storytelling", icon: "📖" },
];

const OUTPUT_FORMATS = [
  { id: "prose", label: "Prose / Paragraphs" },
  { id: "bullet", label: "Bullet points" },
  { id: "numbered", label: "Numbered list" },
  { id: "table", label: "Table" },
  { id: "json", label: "JSON" },
  { id: "markdown", label: "Markdown" },
  { id: "step-by-step", label: "Step-by-step" },
  { id: "qa", label: "Q&A format" },
  { id: "email", label: "Professional Email" },
];

const AI_TARGETS = [
  { id: "chatgpt", label: "ChatGPT / GPT-4o", icon: "🔵" },
  { id: "claude", label: "Claude 3.5", icon: "🟠" },
  { id: "gemini", label: "Gemini 1.5", icon: "🔴" },
  { id: "midjourney", label: "Midjourney v6", icon: "🖼️" },
  { id: "dalle", label: "DALL·E 3", icon: "🌈" },
  { id: "stable", label: "Stable Diffusion", icon: "🌌" },
  { id: "grok", label: "Grok / xAI", icon: "🚀" },
  { id: "general", label: "Any AI", icon: "🌐" },
];

const PROMPT_TEMPLATES = [
  {
    id: "seo-blog",
    label: "SEO Blog Post",
    categoryId: "marketing",
    task: "Write an SEO-friendly blog post",
    role: "Experienced content marketer",
    toneId: "educational",
    outputFormatId: "prose",
    length: "Long-form, ~900 words",
    aiTargetId: "chatgpt",
    audience: "Potential customers researching a solution",
    instruction: "Produce a clear, search-optimized blog post with headings, benefits, and examples.",
    constraints: "Include keywords naturally, use subheadings, and keep paragraphs short.",
    successCriteria: "Includes intro, 3 sections, actionable takeaway, and CTA.",
    tags: ["seo", "blog", "marketing"],
  },
  {
    id: "cold-sales-email",
    label: "Cold Sales Email",
    categoryId: "marketing",
    task: "Write a cold sales email",
    role: "High-converting B2B copywriter",
    toneId: "persuasive",
    outputFormatId: "email",
    length: "Short and compelling (150-200 words)",
    aiTargetId: "chatgpt",
    audience: "Decision makers at mid-market SaaS companies",
    instruction: "Create a concise email that highlights product benefits, handles objections, and ends with a strong call to action.",
    constraints: "Use an approachable tone, avoid jargon, and keep it under 200 words.",
    successCriteria: "Includes a strong opening, value points, clear CTA, and polite close.",
    tags: ["sales", "email", "marketing"],
  },
  {
    id: "product-launch",
    label: "Product Launch Brief",
    categoryId: "business",
    task: "Write a product launch brief",
    role: "Strategic product marketer",
    toneId: "professional",
    outputFormatId: "numbered",
    length: "Medium length (5–7 bullet points)",
    aiTargetId: "chatgpt",
    audience: "Internal launch team and stakeholders",
    instruction: "Summarize the launch plan, positioning, audience, and key milestones.",
    constraints: "Stay organized with headings and keep content actionable.",
    successCriteria: "Deliver a clear launch summary with go-to-market priorities.",
    tags: ["launch", "brief", "business"],
  },
  {
    id: "code-review",
    label: "Code Review Request",
    categoryId: "coding",
    task: "Request a code review",
    role: "Senior software engineer",
    toneId: "professional",
    outputFormatId: "bullet",
    length: "Short and precise",
    aiTargetId: "chatgpt",
    audience: "Fellow engineers",
    instruction: "Ask the AI to review code for bugs, readability, edge cases, and naming.",
    constraints: "Point out issues clearly and suggest improvements without rewriting the entire code.",
    successCriteria: "Provides a list of issues and practical recommendations.",
    tags: ["code", "review", "engineering"],
  },
];

const INGREDIENT_GROUPS = [
  {
    id: "audience",
    label: "Audience",
    items: [
      { label: "Business decision makers", field: "audience", value: "Business decision makers" },
      { label: "Developers", field: "audience", value: "Developers" },
      { label: "Students", field: "audience", value: "Students" },
      { label: "Non-technical readers", field: "audience", value: "Non-technical readers" },
    ],
  },
  {
    id: "goal",
    label: "Goal",
    items: [
      { label: "Persuade", field: "goal", value: "Persuade the reader to take action" },
      { label: "Explain", field: "goal", value: "Explain the idea clearly and simply" },
      { label: "Compare", field: "goal", value: "Compare key options and recommend the best one" },
      { label: "Summarize", field: "goal", value: "Summarize the main points effectively" },
    ],
  },
  {
    id: "style",
    label: "Style",
    items: [
      { label: "Formal", field: "tone", toneId: "professional" },
      { label: "Friendly", field: "tone", toneId: "casual" },
      { label: "Direct", field: "tone", toneId: "authoritative" },
      { label: "Creative", field: "tone", toneId: "creative" },
    ],
  },
  {
    id: "detail",
    label: "Detail level",
    items: [
      { label: "Brief", field: "length", value: "Very concise, under 150 words" },
      { label: "Balanced", field: "length", value: "Balanced detail and clarity" },
      { label: "In-depth", field: "length", value: "Detailed with examples and reasoning" },
    ],
  },
  {
    id: "evidence",
    label: "Evidence",
    items: [
      { label: "Use examples", field: "constraints", value: "Include relevant examples or data." },
      { label: "Avoid jargon", field: "constraints", value: "Avoid jargon and explain terms simply." },
      { label: "Cite sources", field: "constraints", value: "Cite sources or research when available." },
    ],
  },
  {
    id: "structure",
    label: "Structure",
    items: [
      { label: "Intro + bullets", field: "structure", value: "Start with a short introduction, then use bullet points." },
      { label: "Step-by-step", field: "structure", value: "Present the answer as a clear step-by-step plan." },
      { label: "Q&A", field: "structure", value: "Structure the response as a Q&A format." },
    ],
  },
];

const COPY_MODES = [
  { id: "prompt", label: "Copy Prompt" },
  { id: "system", label: "Copy System Prompt" },
  { id: "user", label: "Copy User Prompt" },
  { id: "json", label: "Copy JSON" },
  { id: "markdown", label: "Copy Markdown" },
  { id: "plain", label: "Copy Plain Text" },
];

// ─── Enhanced Prompt Builder ────────────────────────────────────────────────

function buildPrompt({
  category,
  task,
  role,
  context,
  instruction,
  constraints,
  tone,
  outputFormat,
  examples,
  aiTarget,
  length,
  audience,
  goal,
  structure,
  successCriteria,
}) {
  const isImageAI = category?.id === "image";
  const parts = [];

  if (isImageAI) {
    let prompt = [task, instruction, goal, context].filter(Boolean).join(", ");
    if (structure) prompt += `, ${structure}`;
    if (tone) prompt += `, ${tone.label.toLowerCase()} mood`;
    if (constraints) prompt += `, ${constraints}`;
    if (successCriteria) prompt += `, ${successCriteria}`;
    
    const params = aiTarget?.id === "midjourney" 
      ? " --v 6 --stylize 750 --ar 16:9 --q 2" 
      : aiTarget?.id === "dalle" 
        ? ", highly detailed, cinematic lighting" 
        : ", 8k, ultra detailed, masterpiece";

    return prompt + params;
  }

  // Text prompt
  if (role) parts.push(`You are a world-class ${role}.`);
  if (audience) parts.push(`Target audience: ${audience}.`);
  if (goal) parts.push(`Goal: ${goal}.`);
  if (context) parts.push(`Context: ${context}`);

  const mainTask = [task, instruction].filter(Boolean).join(" — ");
  if (mainTask) parts.push(`Task: ${mainTask}`);
  if (structure) parts.push(`Structure: ${structure}.`);

  if (tone) parts.push(`Tone and style: ${tone.label}.`);
  if (length) parts.push(`Response length: ${length}.`);
  if (outputFormat && outputFormat.id !== "prose") {
    parts.push(`Output format: ${outputFormat.label}.`);
  }
  if (constraints) parts.push(`Strict constraints: ${constraints}.`);
  if (successCriteria) parts.push(`Success criteria: ${successCriteria}.`);
  if (examples) parts.push(`Reference examples:\n${examples}`);

  parts.push("Think step-by-step and provide the highest quality output possible.");

  return parts.filter(Boolean).join("\n\n");
}

// ─── Utility Functions ──────────────────────────────────────────────────────

const estimateTokens = (text) => Math.ceil(text.length / 4);

const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {}
};

const loadFromLocalStorage = (key, fallback = []) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : fallback;
  } catch (e) {
    return fallback;
  }
};

// ─── Main Component ─────────────────────────────────────────────────────────

const AIPromptBuilderUI = () => {
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState(null);
  const [task, setTask] = useState("");
  const [role, setRole] = useState("");
  const [context, setContext] = useState("");
  const [instruction, setInstruction] = useState("");
  const [constraints, setConstraints] = useState("");
  const [examples, setExamples] = useState("");
  const [tone, setTone] = useState(null);
  const [outputFormat, setOutputFormat] = useState(null);
  const [aiTarget, setAiTarget] = useState(AI_TARGETS[0]);
  const [length, setLength] = useState("Detailed but concise (~400 words)");
  const [audience, setAudience] = useState("");

  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [enhancedPrompt, setEnhancedPrompt] = useState("");
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhanceError, setEnhanceError] = useState("");
  const [copied, setCopied] = useState("");
  const [promptHistory, setPromptHistory] = useState([]);
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [goal, setGoal] = useState("");
  const [structure, setStructure] = useState("");
  const [successCriteria, setSuccessCriteria] = useState("");
  const [draftPrompt, setDraftPrompt] = useState("");
  const [promptSearch, setPromptSearch] = useState("");
  const [promptSort, setPromptSort] = useState("recent");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copyMode, setCopyMode] = useState(COPY_MODES[0]);

  // Load from localStorage
  useEffect(() => {
    setPromptHistory(loadFromLocalStorage("promptHistory", []));
    setSavedPrompts(loadFromLocalStorage("savedPrompts", []));
  }, []);

  const livePrompt = useMemo(() => buildPrompt({
    category,
    task,
    role,
    context,
    instruction,
    constraints,
    tone,
    outputFormat,
    examples,
    aiTarget,
    length,
    audience,
    goal,
    structure,
    successCriteria,
  }), [category, task, role, context, instruction, constraints, tone, outputFormat, examples, aiTarget, length, audience, goal, structure, successCriteria]);

  const qualityChecks = useMemo(() => [
    { label: "Role included", ok: !!role },
    { label: "Audience specified", ok: !!audience },
    { label: "Task phrase present", ok: !!task },
    { label: "Output format selected", ok: !!outputFormat },
    { label: "Constraints added", ok: !!constraints },
    { label: "Success criteria set", ok: !!successCriteria },
  ], [role, audience, task, outputFormat, constraints, successCriteria]);

  const qualityScore = Math.round((qualityChecks.filter((item) => item.ok).length / qualityChecks.length) * 100);

  const isImageAI = category?.id === "image";

  const handleCategorySelect = (cat) => {
    setCategory(cat);
    setTask("");
    setRole("");
    setContext("");
    setInstruction("");
    setConstraints("");
    setExamples("");
    setTone(null);
    setOutputFormat(null);
    setLength("Detailed but concise (~400 words)");
    setAudience("");
    setGoal("");
    setStructure("");
    setSuccessCriteria("");
    setStep(2);
  };

  const applyTemplate = (id) => {
    const template = PROMPT_TEMPLATES.find((item) => item.id === id);
    if (!template) return;

    const presetCategory = CATEGORIES.find((item) => item.id === template.categoryId) || null;
    const presetTone = TONES.find((item) => item.id === template.toneId) || null;
    const presetFormat = OUTPUT_FORMATS.find((item) => item.id === template.outputFormatId) || null;
    const presetAI = AI_TARGETS.find((item) => item.id === template.aiTargetId) || AI_TARGETS[0];

    setCategory(presetCategory);
    setTask(template.task);
    setRole(template.role);
    setTone(presetTone);
    setOutputFormat(presetFormat);
    setLength(template.length);
    setAudience(template.audience);
    setInstruction(template.instruction);
    setConstraints(template.constraints);
    setSuccessCriteria(template.successCriteria);
    setGoal(template.goal || "");
    setStructure(template.structure || "");
    setAiTarget(presetAI);
    setStep(2);
  };

  const addIngredient = (item) => {
    if (item.field === "tone" && item.toneId) {
      setTone(TONES.find((t) => t.id === item.toneId) || null);
      return;
    }

    if (item.field === "audience") {
      setAudience(item.value);
      return;
    }

    if (item.field === "goal") {
      setGoal(item.value);
      return;
    }

    if (item.field === "structure") {
      setStructure(item.value);
      return;
    }

    if (item.field === "length") {
      setLength(item.value);
      return;
    }

    if (item.field === "constraints") {
      setConstraints((current) => current ? `${current} ${item.value}` : item.value);
      return;
    }
  };

  const formatCopyText = (mode, promptText) => {
    switch (mode.id) {
      case "system":
        return `System prompt:\n${promptText}`;
      case "user":
        return `User prompt:\n${promptText}`;
      case "json":
        return JSON.stringify({ prompt: promptText, tool: aiTarget?.label, category: category?.label }, null, 2);
      case "markdown":
        return "### Prompt\n\n```text\n" + promptText + "\n```\n";
      case "plain":
        return promptText;
      default:
        return promptText;
    }
  };

  const handleCopyMode = (mode) => {
    setCopyMode(mode);
    copyToClipboard(formatCopyText(mode, generatedPrompt), mode.id);
  };

  const handleGenerate = useCallback(() => {
    const prompt = buildPrompt({
      category,
      task,
      role,
      context,
      instruction,
      constraints,
      tone,
      outputFormat,
      examples,
      aiTarget,
      length,
      audience,
      goal,
      structure,
      successCriteria,
    });

    setGeneratedPrompt(prompt);
    setEnhancedPrompt("");
    setStep(3);

    const historyEntry = {
      id: Date.now(),
      prompt,
      category: category?.label,
      task,
      aiTarget: aiTarget?.label,
      timestamp: new Date().toISOString(),
    };

    const newHistory = [historyEntry, ...promptHistory.slice(0, 19)];
    setPromptHistory(newHistory);
    saveToLocalStorage("promptHistory", newHistory);
  }, [category, task, role, context, instruction, constraints, tone, outputFormat, examples, aiTarget, length, audience, goal, structure, successCriteria, promptHistory]);

  const enhancePrompt = async (style = "standard") => {
    if (!generatedPrompt) return;
    
    setIsEnhancing(true);
    setEnhanceError("");

    try {
      // Mock enhancement with realistic improvements (in production replace with real API)
      await new Promise(resolve => setTimeout(resolve, 800));

      let enhanced = generatedPrompt;

      if (style === "detailed") {
        enhanced += "\n\nProvide extremely detailed reasoning, cite sources where possible, and include multiple examples.";
      } else if (style === "concise") {
        enhanced = enhanced.replace(/detailed/gi, "concise") + "\n\nBe extremely concise and actionable.";
      } else {
        enhanced += "\n\nUse Chain of Thought reasoning. Prioritize clarity, usefulness, and creativity.";
      }

      setEnhancedPrompt(enhanced);
    } catch (e) {
      setEnhanceError("Enhancement failed. Please check your connection.");
    } finally {
      setIsEnhancing(false);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1800);
  };

  const savePrompt = () => {
    if (!generatedPrompt) return;
    
    const saved = {
      id: Date.now(),
      prompt: generatedPrompt,
      enhanced: enhancedPrompt,
      category: category?.label,
      task,
      audience,
      successCriteria,
      tags: [],
      timestamp: new Date().toISOString(),
    };

    const newSaved = [saved, ...savedPrompts.slice(0, 29)];
    setSavedPrompts(newSaved);
    saveToLocalStorage("savedPrompts", newSaved);
  };

  const reset = () => {
    setStep(1);
    setCategory(null);
    setTask(""); setRole(""); setContext(""); setInstruction("");
    setConstraints(""); setExamples(""); setTone(null);
    setOutputFormat(null); setAudience("");
    setGoal(""); setStructure(""); setSuccessCriteria("");
    setGeneratedPrompt(""); setEnhancedPrompt("");
    setDraftPrompt(""); setPromptSearch(""); setShowAdvanced(false);
    setCopyMode(COPY_MODES[0]);
  };

  const loadSaved = (saved) => {
    setGeneratedPrompt(saved.prompt);
    setEnhancedPrompt(saved.enhanced || "");
    setStep(3);
  };

  // Computed values
  const tokenCount = useMemo(() => estimateTokens(generatedPrompt), [generatedPrompt]);

  // ─── Sub Components ─────────────────────────────────────────────────────

  const StepIndicator = () => (
    <div className="d-flex justify-content-center mb-4">
      {[1, 2, 3].map((s) => (
        <React.Fragment key={s}>
          <div
            onClick={() => s < step && setStep(s)}
            className={`px-4 py-2 rounded-pill fw-semibold cursor-pointer transition-all ${
              s === step ? "bg-primary text-white shadow" : s < step ? "bg-success text-white" : "bg-light text-muted"
            }`}
          >
            {s < step ? "✓" : s} {s === 1 ? "Category" : s === 2 ? "Details" : "Ready"}
          </div>
          {s < 3 && <div className="flex-grow-1 border-top border-2 border-light mx-3 mt-3" />}
        </React.Fragment>
      ))}
    </div>
  );

  const IngredientChips = () => (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0 text-muted">Prompt Ingredients</h6>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowAdvanced((value) => !value)}>
          {showAdvanced ? "Hide advanced" : "Show advanced"}
        </button>
      </div>
      {INGREDIENT_GROUPS.map((group) => (
        <div key={group.id} className="mb-3">
          <small className="text-uppercase text-muted">{group.label}</small>
          <div className="d-flex flex-wrap gap-2 mt-2">
            {group.items.map((item) => (
              <button
                key={item.label}
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => addIngredient(item)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const QuickTemplates = () => (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 text-muted">Prompt Recipes</h6>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowAdvanced((value) => !value)}>
          {showAdvanced ? "Hide recipes" : "Show recipes"}
        </button>
      </div>
      <div className="row g-2">
        {PROMPT_TEMPLATES.map((tmpl) => (
          <div key={tmpl.id} className="col-6 col-lg-3">
            <button
              type="button"
              onClick={() => applyTemplate(tmpl.id)}
              className="btn btn-outline-secondary w-100 text-start h-100"
            >
              <strong>{tmpl.label}</strong>
              <p className="small text-muted mb-0">{tmpl.tags.join(", ")}</p>
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  // Main Render
  return (
    <div className="converter-container p-3">
      <div className="card shadow-lg border-0">
        <div className="card-header text-white py-4" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1 fw-bold">
                <i className="bi bi-magic me-3"></i>AI Prompt Forge
              </h4>
              <small className="opacity-75">Craft better prompts • Get better results</small>
            </div>
            <div className="d-flex gap-2">
              <button
                onClick={() => setShowLibrary(!showLibrary)}
                className="btn btn-light btn-sm"
              >
                <i className="bi bi-book me-1"></i>Library
              </button>
              <button onClick={reset} className="btn btn-light btn-sm">
                <i className="bi bi-arrow-counterclockwise"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="card-body">
          <StepIndicator />

          {step === 1 && (
            <div>
              <h5 className="mb-4 text-center">What are you creating today?</h5>
              <div className="row g-3">
                {CATEGORIES.map((cat) => (
                  <div key={cat.id} className="col-6 col-lg-3">
                    <div
                      onClick={() => handleCategorySelect(cat)}
                      className={`card h-100 border-2 hover-lift ${category?.id === cat.id ? `border-${cat.color} bg-${cat.color} bg-opacity-10` : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="card-body text-center py-4">
                        <i className={`bi ${cat.icon} display-4 text-${cat.color} mb-3`} />
                        <h6>{cat.label}</h6>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <QuickTemplates />
            </div>
          )}

          {step === 2 && category && (
            <div className="row g-4">
              <div className="col-lg-8">
                <div className="mb-4">
                  <label className="form-label fw-bold">What do you want the AI to do? <span className="text-danger">*</span></label>
                  <textarea
                    className="form-control"
                    rows={4}
                    placeholder="Describe the task in plain language, e.g. Write a persuasive product email for busy founders..."
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                  />
                </div>

                <div className="row g-3 mb-4">
                  {!isImageAI && (
                    <>
                      <div className="col-md-6">
                        <label className="form-label">Audience</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Non-technical readers, startup founders, students..."
                          value={audience}
                          onChange={(e) => setAudience(e.target.value)}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Goal</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="What should success look like?"
                          value={goal}
                          onChange={(e) => setGoal(e.target.value)}
                        />
                      </div>
                    </>
                  )}

                  <div className="col-12">
                    <label className="form-label">Success Criteria</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Must be under 200 words, avoid jargon, include 3 options..."
                      value={successCriteria}
                      onChange={(e) => setSuccessCriteria(e.target.value)}
                    />
                  </div>
                </div>

                <IngredientChips />

                {showAdvanced && (
                  <div className="card mb-4 border-secondary">
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Structure</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Intro with bullets, step-by-step, Q&A..."
                            value={structure}
                            onChange={(e) => setStructure(e.target.value)}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Length</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Balanced, brief, detailed..."
                            value={length}
                            onChange={(e) => setLength(e.target.value)}
                          />
                        </div>
                        <div className="col-12">
                          <label className="form-label">Examples or reference</label>
                          <textarea
                            className="form-control"
                            rows={3}
                            placeholder="Paste an example prompt or describe a style reference..."
                            value={examples}
                            onChange={(e) => setExamples(e.target.value)}
                          />
                        </div>
                        <div className="col-12">
                          <label className="form-label">Constraints</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Keep tone friendly, avoid technical language, no list format..."
                            value={constraints}
                            onChange={(e) => setConstraints(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1">Live Prompt Preview</h6>
                        <small className="text-muted">See the prompt update as you type.</small>
                      </div>
                      <span className={`badge ${qualityScore > 75 ? "bg-success" : qualityScore > 45 ? "bg-warning" : "bg-danger"}`}>
                        {qualityScore}%
                      </span>
                    </div>
                  </div>
                  <div className="card-body">
                    <textarea className="form-control font-monospace" rows={10} value={livePrompt} readOnly />
                    <div className="mt-3">
                      <div className="d-flex flex-wrap gap-2">
                        {qualityChecks.map((item) => (
                          <span
                            key={item.label}
                            className={`badge ${item.ok ? "bg-success text-white" : "bg-secondary text-white"}`}
                          >
                            {item.ok ? "✓" : "•"} {item.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="sticky-top" style={{ top: "20px" }}>
                  <div className="mb-4">
                    <label className="form-label">Tone</label>
                    <div className="d-flex flex-wrap gap-2">
                      {TONES.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setTone(tone?.id === t.id ? null : t)}
                          className={`btn btn-sm ${tone?.id === t.id ? "btn-dark" : "btn-outline-secondary"}`}
                        >
                          {t.icon} {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {!isImageAI && (
                    <div className="mb-4">
                      <label className="form-label">Output Format</label>
                      <select
                        className="form-select"
                        value={outputFormat?.id || ""}
                        onChange={(e) => setOutputFormat(OUTPUT_FORMATS.find((f) => f.id === e.target.value) || null)}
                      >
                        <option value="">Default</option>
                        {OUTPUT_FORMATS.map((f) => (
                          <option key={f.id} value={f.id}>{f.label}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="form-label">Target AI</label>
                    <div className="d-flex flex-wrap gap-2">
                      {AI_TARGETS.filter((t) =>
                        isImageAI ? ["midjourney", "dalle", "stable", "general"].includes(t.id) : !["midjourney", "dalle", "stable"].includes(t.id)
                      ).map((t) => (
                        <button
                          type="button"
                          key={t.id}
                          onClick={() => setAiTarget(t)}
                          className={`btn btn-sm ${aiTarget.id === t.id ? "btn-primary" : "btn-outline-primary"}`}
                        >
                          {t.icon} {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={!instruction}
                    className={`btn btn-${category.color} w-100 py-3 fw-bold fs-5 shadow-sm`}
                  >
                    <i className="bi bi-lightning-charge me-2"></i>
                    Generate Prompt
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="d-flex gap-3 mb-4">
                <button onClick={() => setStep(2)} className="btn btn-outline-secondary">
                  ← Edit
                </button>
                <button onClick={savePrompt} className="btn btn-outline-success">
                  <i className="bi bi-bookmark-plus me-1"></i>Save
                </button>
                <button onClick={reset} className="btn btn-outline-danger ms-auto">
                  Start Over
                </button>
              </div>

              {/* Generated Prompt */}
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center bg-light">
                  <h6>Your Prompt • {tokenCount} tokens</h6>
                  <button onClick={() => copyToClipboard(generatedPrompt, "base")} className="btn btn-sm btn-primary">
                    <i className={`bi bi-${copied === "base" ? "check-lg" : "clipboard"} me-1`}></i>
                    {copied === "base" ? "Copied" : "Copy"}
                  </button>
                </div>
                <div className="card-body">
                  <textarea
                    className="form-control font-monospace"
                    rows={12}
                    value={generatedPrompt}
                    onChange={(e) => setGeneratedPrompt(e.target.value)}
                  />
                </div>
              </div>

              {/* Enhancement Section */}
              <div className="card border-primary mb-4">
                <div className="card-header bg-primary text-white d-flex justify-content-between">
                  <h6 className="mb-0">🔥 Enhanced Versions</h6>
                  <div className="btn-group btn-group-sm">
                    <button onClick={() => enhancePrompt("standard")} disabled={isEnhancing} className="btn btn-light">Standard</button>
                    <button onClick={() => enhancePrompt("detailed")} disabled={isEnhancing} className="btn btn-light">Detailed</button>
                    <button onClick={() => enhancePrompt("concise")} disabled={isEnhancing} className="btn btn-light">Concise</button>
                  </div>
                </div>
                <div className="card-body">
                  {enhancedPrompt ? (
                    <>
                      <textarea className="form-control font-monospace mb-3" rows={14} value={enhancedPrompt} onChange={(e) => setEnhancedPrompt(e.target.value)} />
                      <button onClick={() => copyToClipboard(enhancedPrompt, "enhanced")} className="btn btn-success">
                        Copy Enhanced Prompt
                      </button>
                    </>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      <i className="bi bi-stars display-4 mb-3 d-block"></i>
                      <p>Click one of the enhancement buttons above to get a better version</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Saved Prompts & History Sidebar */}
      {(promptHistory.length > 0 || savedPrompts.length > 0) && (
        <div className="mt-5">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
            <div>
              <h5 className="mb-1">Your Prompt Library</h5>
              <small className="text-muted">Search, sort, and reuse your best prompts.</small>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Search prompts..."
                value={promptSearch}
                onChange={(e) => setPromptSearch(e.target.value)}
                style={{ minWidth: "240px" }}
              />
              <select
                className="form-select form-select-sm"
                value={promptSort}
                onChange={(e) => setPromptSort(e.target.value)}
              >
                <option value="recent">Recent</option>
                <option value="a-z">A → Z</option>
                <option value="z-a">Z → A</option>
              </select>
            </div>
          </div>

          <div className="row g-3">
            {savedPrompts
              .filter((p) => p.prompt.toLowerCase().includes(promptSearch.toLowerCase()) || p.task.toLowerCase().includes(promptSearch.toLowerCase()))
              .sort((a, b) => {
                if (promptSort === "a-z") return a.task.localeCompare(b.task);
                if (promptSort === "z-a") return b.task.localeCompare(a.task);
                return b.timestamp.localeCompare(a.timestamp);
              })
              .slice(0, 6)
              .map((p) => (
                <div key={p.id} className="col-md-6 col-lg-4">
                  <div className="card h-100" style={{ cursor: "pointer" }}>
                    <div className="card-body" onClick={() => loadSaved(p)}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-1">{p.task || "Saved prompt"}</h6>
                          <small className="text-muted">{p.category || "No category"}</small>
                        </div>
                        <span className="badge bg-secondary">{new Date(p.timestamp).toLocaleDateString()}</span>
                      </div>
                      <p className="text-truncate mb-2">{p.prompt.substring(0, 120)}...</p>
                      <div className="d-flex flex-wrap gap-2">
                        <span className="badge bg-light text-dark">{p.audience || "No audience"}</span>
                        <span className="badge bg-light text-dark">{p.successCriteria ? "Success criteria" : "No criteria"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export class AIPromptBuilder extends BaseConverter {
  constructor() {
    super("ai-prompt-builder", "AI Prompt Forge", "bi-magic");
  }

  render(container) {
    if (!this.root) this.root = createRoot(container);
    this.root.render(<AIPromptBuilderUI />);
  }
}